/**
 * Uygulama kökü — yönlendirme ve oturum kapısı.
 *
 * Kapı mantığı: oturum yoksa hangi adrese gidilirse gidilsin giriş ekranı
 * gösterilir. Adres korunur ki giriş sonrası kullanıcı gitmek istediği yerde
 * kalsın (bir faturanın linkini paylaşmak işe yarasın).
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import type { ReactNode } from 'react';
import { OturumSaglayici, useOturum } from './oturum';
import { izinVar } from './lib/yetki';
import { OturumHatasi } from './lib/api';
import { Giris } from './screens/Giris';
import { Panosu } from './screens/Panosu';
import { FaturaListesi } from './screens/FaturaListesi';
import { FaturaDetayi } from './screens/FaturaDetayi';
import { Kullanicilar } from './screens/Kullanicilar';
import { Ayarlar } from './screens/Ayarlar';
import { Kurulum } from './screens/Kurulum';
import { Sablonlar } from './screens/Sablonlar';
import { GonderimKayitlari } from './screens/GonderimKayitlari';
import { DenetimKaydi } from './screens/DenetimKaydi';
import { HataKayitlari } from './screens/HataKayitlari';
import { OnayBekleyen } from './screens/OnayBekleyen';
import { Roller } from './screens/Roller';
import { YetkiYok } from './screens/YetkiYok';
import { KurulumBekleniyor } from './screens/KurulumBekleniyor';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { api } from './lib/api';
import { kilitteAcikMi } from './lib/kurulum';

const sorguIstemcisi = new QueryClient({
  defaultOptions: {
    queries: {
      // Oturum bittiyse tekrar denemek anlamsız: kullanıcı giriş yapmalı.
      retry: (deneme, hata) => !(hata instanceof OturumHatasi) && deneme < 2,
      // Sekmeye geri dönünce liste kendiliğinden tazelensin.
      refetchOnWindowFocus: true,
      staleTime: 5_000,
    },
    mutations: {
      // Yazma işlemleri tekrar DENENMEZ: iki kez fatura göndermek kabul edilemez.
      retry: false,
    },
  },
});

/**
 * Belirli bir İZİN isteyen ekranları sarar.
 *
 * Sunucu zaten 403 döner; bu kapı kullanıcıyı çıplak bir hata ekranına
 * düşürmek yerine "yetkiniz yok" sayfasına alır — hata değil, açıklamadır.
 * Güvenlik değeri YOKTUR, sadece nezakettir (bkz. src/lib/yetki.ts).
 */
function IzinKapisi({ izin, children }: { izin: string; children: ReactNode }) {
  const { kullanici } = useOturum();
  if (!izinVar(kullanici, izin)) return <YetkiYok izin={izin} />;
  return <>{children}</>;
}

/**
 * Kurulum kapısı — kurulum tamamlanmadan panelde HİÇBİR yere girilmez.
 *
 * Kullanıcı kuralı. İki yüz:
 *   - Ayar yetkisi olan → Kurulum ekranına yönlendirilir (ve Ayarlar açık kalır,
 *     eksiği orada düzeltir). Menü de bu ikisine daralır (Kabuk).
 *   - Ayar yetkisi olmayan → kilit ekranı; ne düzeltebilir ne ayrıntı görür.
 *
 * Kaynak `/api/setup/ozet` (oturumlu herkes). Ayrıntı taşımaz; yalnızca kilit.
 * Sorgu sürerken içerik gösterilmez — bir an panoyu gösterip sonra kilitlemek
 * "girdim, sonra atıldım" hissi verir.
 */
function KurulumKapisi({ children }: { children: ReactNode }) {
  const { kullanici } = useOturum();
  const konum = useLocation();
  const { data, isPending, isError } = useQuery({
    queryKey: ['kurulum-ozet'],
    queryFn: api.kurulumOzeti,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Uç ulaşılamıyorsa kilitlemek yerine geçir: kilit bir güvenlik önlemi değil,
  // yönlendirmedir — asıl kilit sunucuda (kapı + motor). Kullanıcıyı kilitli
  // bırakıp hata ekranını da görmesini engellemek daha kötü.
  if (isError) return <>{children}</>;
  if (isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--ground)]">
        <div
          className="size-6 animate-spin rounded-full border-2 border-[var(--line-strong)] border-t-[var(--accent)]"
          role="status"
          aria-label="Yükleniyor"
        />
      </div>
    );
  }
  if (data.faturaKesilebilir) return <>{children}</>;

  /*
   * Süper yönetici muaf: sistemi kurmadan da her ekranı gezip TEST edebilmeli.
   * Yalnızca gezinme serbestliği — fatura kesme kapısı sunucuda ve herkese
   * aynı: kurulum bitmeden fatura yine kesilmez.
   */
  if (kullanici?.isSuperAdmin) return <>{children}</>;

  if (!izinVar(kullanici, 'ayar.goruntule')) return <KurulumBekleniyor />;

  if (!kilitteAcikMi(konum.pathname)) return <Navigate to="/kurulum" replace />;
  return <>{children}</>;
}

function Kapi() {
  const { yukleniyor, kullanici } = useOturum();

  if (yukleniyor) {
    // İlk sorgu sürerken ne giriş ne panel gösterilir: ekran titremesin.
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--ground)]">
        <div
          className="size-6 animate-spin rounded-full border-2 border-[var(--line-strong)] border-t-[var(--accent)]"
          role="status"
          aria-label="Yükleniyor"
        />
      </div>
    );
  }

  if (!kullanici) return <Giris />;

  return (
    <KurulumKapisi>
      <Routes>
        <Route path="/" element={<Panosu />} />
        <Route path="/faturalar" element={<FaturaListesi />} />
        <Route path="/faturalar/:kod" element={<FaturaDetayi />} />
        <Route
          path="/kullanicilar"
          element={
            <IzinKapisi izin="kullanici.goruntule">
              <Kullanicilar />
            </IzinKapisi>
          }
        />
        <Route
          path="/roller"
          element={
            <IzinKapisi izin="rol.goruntule">
              <Roller />
            </IzinKapisi>
          }
        />
        <Route
          path="/onay"
          element={
            <IzinKapisi izin="fatura.goruntule">
              <OnayBekleyen />
            </IzinKapisi>
          }
        />
        <Route
          path="/hatalar"
          element={
            <IzinKapisi izin="hata.goruntule">
              <HataKayitlari />
            </IzinKapisi>
          }
        />
        <Route
          path="/denetim"
          element={
            <IzinKapisi izin="kayit.goruntule">
              <DenetimKaydi />
            </IzinKapisi>
          }
        />
        <Route
          path="/sablonlar"
          element={
            <IzinKapisi izin="sablon.goruntule">
              <Sablonlar />
            </IzinKapisi>
          }
        />
        <Route
          path="/gonderimler"
          element={
            <IzinKapisi izin="bildirim.goruntule">
              <GonderimKayitlari />
            </IzinKapisi>
          }
        />
        <Route
          path="/kurulum"
          element={
            <IzinKapisi izin="ayar.goruntule">
              <Kurulum />
            </IzinKapisi>
          }
        />
        <Route
          path="/ayarlar"
          element={
            <IzinKapisi izin="ayar.goruntule">
              <Ayarlar />
            </IzinKapisi>
          }
        />
        {/* Bilinmeyen adres: kullanıcıyı boş ekranda bırakmak yerine listeye al. */}
        <Route path="*" element={<Navigate to="/faturalar" replace />} />
      </Routes>
    </KurulumKapisi>
  );
}

export function App() {
  return (
    <QueryClientProvider client={sorguIstemcisi}>
      <BrowserRouter>
        <OturumSaglayici>
          <Kapi />
        </OturumSaglayici>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
